import { useTranslation } from "react-i18next";
import { Link, useLocation } from "react-router-dom";

type SocialLink = {
  name: string;
  href: string;
  icon?: string;
  img?: string;
};

const socialLinks: SocialLink[] = [
  {
    name: "LinkedIn",
    icon: "ri-linkedin-fill",
    href: "https://www.linkedin.com/company/netbasis-international",
  },
  {
    name: "Xing",
    icon: "ri-xing-fill",
    href: "https://www.xing.com/companies/netbasisinternational",
  },
  {
    name: "freelancermap",
    href: "https://www.freelancermap.de/profil/christof-guenther",
    img: "https://public.readdy.ai/ai/img_res/5c41a053-6aa7-465a-adbe-29cc2320d392.png",
  },
  {
    name: "freelance.de",
    href: "https://www.freelance.de/freelancer/232789-International-Business-Development",
    img: "https://public.readdy.ai/ai/img_res/a98b9f48-c885-4416-9d9b-c2808ee2a0df.png",
  },
];

export default function Footer() {
  const { t } = useTranslation("home");
  const location = useLocation();

  // Sprache direkt aus der URL ableiten — kein flüchtiger Zustand, keine Überraschungen
  const currentLang = (() => {
    const path = location.pathname;
    if (path.startsWith("/en")) return "en";
    if (path.startsWith("/fr")) return "fr";
    if (path.startsWith("/es")) return "es";
    return "de";
  })();

  const getPrivacyHref = () => {
    if (currentLang === "en") return "/en/privacy";
    if (currentLang === "fr") return "/fr/politique-de-confidentialite";
    if (currentLang === "es") return "/es/politica-de-privacidad";
    return "/de/datenschutz";
  };

  const getImprintHref = () => {
    if (currentLang === "en") return "/en/legal-notice";
    if (currentLang === "fr") return "/fr/mentions-legales";
    if (currentLang === "es") return "/es/aviso-legal";
    return "/de/impressum";
  };

  const getContactHref = () => {
    if (currentLang === "en") return "/en/contact";
    if (currentLang === "fr") return "/fr/contact";
    if (currentLang === "es") return "/es/contacto";
    return "/de/kontakt";
  };

  const footerLinks = [
    { label: t("footer.privacy"), href: getPrivacyHref() },
    { label: t("footer.contact"), href: getContactHref() },
    { label: t("footer.imprint"), href: getImprintHref() },
  ];

  return (
    <footer className="bg-white">
      {/* Top Section - Social + Links */}
      <div className="py-8 md:py-12 px-4 md:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-6">
          {/* Social Icons */}
          <div className="flex items-center gap-3">
            {socialLinks.map((social) => (
              <a
                key={social.name}
                href={social.href}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-500 hover:bg-primary-600 transition-colors cursor-pointer"
                aria-label={social.name}
              >
                {social.icon ? (
                  <i className={`${social.icon} text-white text-lg`} />
                ) : (
                  <img
                    src={social.img}
                    alt={social.name}
                    className="w-7 h-7 object-contain"
                    style={{ filter: "brightness(0) invert(1)" }}
                  />
                )}
              </a>
            ))}
          </div>

          {/* Footer Links */}
          <nav className="flex items-center gap-4 md:gap-6">
            {footerLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                className={`font-heading ${currentLang === "fr" ? "text-xs sm:text-sm" : "text-sm"} text-primary-600 hover:text-primary-700 transition-colors cursor-pointer whitespace-nowrap`}
              >
                {link.label}
              </Link>
            ))}
          </nav>
        </div>
      </div>

      {/* Bottom Section - Copyright */}
      <div className="py-4 md:py-6 px-4 md:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto text-center">
          <p className="text-sm text-foreground-950">
            {t("footer.copyright", { year: 2026 })}
          </p>
        </div>
      </div>
    </footer>
  );
}